'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Key stored in localStorage right after registration to signal a fresh trial was just granted
export const TRIAL_PENDING_KEY = 'party_games_trial_pending';

interface Subscription {
  tier: 'free' | 'day' | 'week';
  subscription_status: 'active' | 'cancelled' | 'expired' | 'trialing' | 'past_due';
  current_period_end: string | null;
}

export function useSubscription() {
  const { user, registerSubscriptionRefresh, forcePremium } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const clearRetryTimer = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const fetchSubscription = useCallback(
    async (attempt = 0) => {
      if (!user) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      if (attempt === 0) setLoading(true);

      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('tier, subscription_status, current_period_end')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          const sub = data as Subscription;

          const isValidPremium = (() => {
            if (sub.tier === 'free') return false;
            const status = sub.subscription_status;
            if (status !== 'active' && status !== 'trialing') return false;
            if (sub.current_period_end) {
              const end = new Date(sub.current_period_end);
              if (end < new Date()) return false;
            }
            return true;
          })();

          const trialPending = (() => {
            try { return localStorage.getItem(TRIAL_PENDING_KEY) === user.id; } catch { return false; }
          })();

          if (trialPending && isValidPremium) {
            try { localStorage.removeItem(TRIAL_PENDING_KEY); } catch { /* ignore */ }
          }

          setSubscription(sub);
          setLoading(false);
          return;
        }

        const trialPending = (() => {
          try { return localStorage.getItem(TRIAL_PENDING_KEY) === user.id; } catch { return false; }
        })();

        const RETRY_DELAYS = [300, 600, 1000, 1500, 2000, 3000, 4000, 5000, 6000, 8000];
        const maxAttempts = trialPending ? RETRY_DELAYS.length : 5;

        if (attempt < maxAttempts) {
          retryTimerRef.current = setTimeout(() => {
            fetchSubscription(attempt + 1);
          }, RETRY_DELAYS[attempt] ?? 3000);
        } else {
          setSubscription(null);
          setLoading(false);
        }
      } catch {
        setSubscription(null);
        setLoading(false);
      }
    },
    [user] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const fetchSubscriptionRef = useRef(fetchSubscription);
  useEffect(() => {
    fetchSubscriptionRef.current = fetchSubscription;
  }, [fetchSubscription]);

  const refetch = useCallback(() => {
    clearRetryTimer();
    fetchSubscriptionRef.current(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!registerSubscriptionRefresh) return;
    const unregister = registerSubscriptionRefresh(refetch);
    return unregister;
  }, [registerSubscriptionRefresh, refetch]);

  useEffect(() => {
    clearRetryTimer();
    fetchSubscriptionRef.current(0);
    return () => clearRetryTimer();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Realtime subscription effect.
  //
  // Root cause of the "cannot add postgres_changes callbacks after subscribe()" error:
  // React StrictMode mounts → unmounts → remounts every effect. The cleanup calls
  // supabase.removeChannel() which is async. If the second mount fires before the
  // async removal completes, Supabase still considers the channel "subscribed" and
  // throws when .on() is called again.
  //
  // Fix: generate a truly unique channel name per effect invocation (not per render)
  // using a local variable, and guard with a `cancelled` flag so the cleanup from
  // the first StrictMode run prevents the second run from ever calling .subscribe().
  useEffect(() => {
    if (!user) return;

    // Unique name per effect invocation — avoids any channel name collision
    const channelName = `subscriptions:${user.id}:${Math.random().toString(36).slice(2)}`;
    let cancelled = false;

    // Remove any previously active channel first (fire-and-forget is fine here
    // because we're using a brand-new unique channel name, so there's no collision)
    const existingChannels = supabase.getChannels();
    existingChannels.forEach((ch) => {
      if (ch.topic.startsWith(`realtime:subscriptions:${user.id}`)) {
        supabase.removeChannel(ch);
      }
    });

    // Build the channel object but don't subscribe yet
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          if (cancelled) return;
          clearRetryTimer();
          fetchSubscriptionRef.current(0);
        }
      );

    // Only subscribe if this effect invocation hasn't been cancelled by cleanup
    if (!cancelled) {
      channel.subscribe();
    }

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPremium = (() => {
    // If forcePremium is set (e.g. right after registration), immediately return true
    if (forcePremium) return true;
    if (!subscription) return false;
    if (subscription.tier === 'free') return false;
    const status = subscription.subscription_status;
    if (status !== 'active' && status !== 'trialing') return false;
    if (subscription.current_period_end) {
      const end = new Date(subscription.current_period_end);
      if (end < new Date()) return false;
    }
    return true;
  })();

  return { subscription, isPremium, loading, refetch };
}
